import React, { useState } from "react";
import { Alert, Button, FormFeedback, Input, Spinner } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { submitFeedback } from "../../helpers/backend_helper";
import HeaderSidebarToggle from "../../Components/Common/HeaderSidebarToggle";
import HomeLogoLink from "../../Components/Common/HomeLogoLink";

type StatusMessage = {
    type: "success" | "danger";
    message: string;
};

const FeedbackPage = () => {
    const [status, setStatus] = useState<StatusMessage | null>(null);
    const [loading, setLoading] = useState(false);

    const validation = useFormik({
        initialValues: {
            title: "",
            content: "",
        },
        validationSchema: Yup.object({
            title: Yup.string().required("Vui lòng nhập tiêu đề").max(255, "Tối đa 255 ký tự"),
            content: Yup.string().required("Vui lòng nhập nội dung").max(2000, "Tối đa 2000 ký tự"),
        }),
        onSubmit: async (values, { resetForm, setErrors }) => {
            setLoading(true);
            setStatus(null);

            try {
                await submitFeedback({
                    title: values.title.trim(),
                    content: values.content.trim(),
                });

                resetForm();
                setStatus({ type: "success", message: "Đã gửi góp ý thành công. Cảm ơn bạn!" });
            } catch (error: any) {
                const fieldErrors: Record<string, string> = {};
                const errors = error?.errors ?? error?.data?.errors;

                if (errors && typeof errors === "object") {
                    Object.entries(errors).forEach(([field, messages]) => {
                        if (Array.isArray(messages) && messages[0]) {
                            fieldErrors[field] = String(messages[0]);
                        } else if (messages) {
                            fieldErrors[field] = String(messages);
                        }
                    });
                }

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                }

                setStatus({
                    type: "danger",
                    message:
                        error?.message ||
                        error?.data?.message ||
                        "Không thể gửi góp ý. Vui lòng thử lại.",
                });
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <React.Fragment>
            <div className="header-area" id="headerArea">
                <div className="container h-100 d-flex text-center align-items-center justify-content-between rtl-flex-d-row-r">
                    <HomeLogoLink className="me-2" />
                    <div className="page-heading">
                        <h6 className="mb-0">Góp ý phần mềm</h6>
                    </div>
                    <HeaderSidebarToggle className="ms-2" />
                </div>
            </div>

            <div className="page-content-wrapper">
                <div className="container">
                    <div className="profile-wrapper-area py-3">
                        <div className="card user-data-card">
                            <div className="card-body">
                                {status && <Alert color={status.type}>{status.message}</Alert>}

                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        validation.handleSubmit();
                                    }}
                                >
                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-format-title"></i>
                                            <span>Tiêu đề</span>
                                        </div>
                                        <Input
                                            type="text"
                                            name="title"
                                            placeholder="Nhập tiêu đề góp ý"
                                            value={validation.values.title}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={!!(validation.touched.title && validation.errors.title)}
                                        />
                                        {validation.touched.title && validation.errors.title ? (
                                            <FormFeedback type="invalid">{validation.errors.title}</FormFeedback>
                                        ) : null}
                                    </div>

                                    <div className="mb-3">
                                        <div className="title mb-2">
                                            <i className="mdi mdi-text-box-outline"></i>
                                            <span>Nội dung</span>
                                        </div>
                                        <Input
                                            type="textarea"
                                            name="content"
                                            rows={5}
                                            placeholder="Nhập nội dung góp ý"
                                            value={validation.values.content}
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            invalid={!!(validation.touched.content && validation.errors.content)}
                                        />
                                        {validation.touched.content && validation.errors.content ? (
                                            <FormFeedback type="invalid">{validation.errors.content}</FormFeedback>
                                        ) : null}
                                    </div>

                                    <Button type="submit" color="primary" className="btn-lg w-100" disabled={loading}>
                                        {loading && <Spinner size="sm" className="me-2" />}
                                        Gửi góp ý
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default FeedbackPage;
